import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance'; // Import axios instance
import { useNavigate } from 'react-router-dom';
import './AddUser.css'; // Ensure this CSS file exists for styling

const AddUserPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState(''); // Auto-filled from email
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Hardcoded role for System Admin
  const role = 'SystemAdmin';

  // Function to validate form inputs
  const validateForm = () => {
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setErrorMessage('All fields are required');
      return false;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    // Basic email format check
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrorMessage('Invalid email format');
      return false;
    }
    // Password length check
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return false;
    }
    return true;
  };

  // Automatically set the username when the email is updated
  useEffect(() => {
    if (email) {
      setUsername(email);
    } else {
      setUsername('');
    }
  }, [email]);

  // Function to handle adding a new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const response = await axiosInstance.post(
        'user', // Relative path since baseURL is set in axiosInstance
        {
          firstName,
          lastName,
          username,
          email,
          password,
          role, // Automatically set to 'SystemAdmin'
          // Include any other required fields here
        }
      );

      if (response.status === 201) {
        setSuccessMessage('User added successfully!');
        // Optionally, navigate to the Manage Users page or clear the form
        setTimeout(() => navigate('/admin/users'), 2000);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      if (error.response && error.response.status === 409) {
        setErrorMessage('A user with this email or username already exists.');
      } else {
        setErrorMessage(
          error.response?.data?.message || 'An error occurred while adding the user.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-user-container">
      <div className="add-user-box">
        <h2>Add New System Admin</h2>
        <form onSubmit={handleAddUser}>
          <input
            type="text"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isLoading}
            required
          />
          <input
            type="text"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isLoading}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            required
          />
          <input
            type="text"
            placeholder="Username (auto-filled from email)"
            value={username}
            disabled={true}
          />
          <input
            type="password"
            placeholder="Password (min 8 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            required
          />
          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading}
            required
          />

          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Adding User...' : 'Add User'}
          </button>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
        </form>
      </div>
    </div>
  );
};

export default AddUserPage;
